// URLs that grant access must not reach analytics. `/order-success?oid=<public_id>`
// opens an order with the customer's name and address to anyone holding it, and
// PostHog records every page URL ($current_url, $referrer, initial and entry URLs).

const CAPABILITY_QUERY_PARAMS = ['oid'] as const;
const REDACTED = 'redacted';

/** Replaces capability query parameters in an absolute or relative URL. */
export const redactUrl = (value: string): string => {
  if (!CAPABILITY_QUERY_PARAMS.some((param) => value.includes(`${param}=`))) return value;

  try {
    const isAbsolute = /^[a-z][a-z\d+.-]*:/i.test(value);
    const url = new URL(value, 'https://astroji.com');
    let changed = false;
    for (const param of CAPABILITY_QUERY_PARAMS) {
      if (url.searchParams.has(param)) {
        url.searchParams.set(param, REDACTED);
        changed = true;
      }
    }
    if (!changed) return value;
    return isAbsolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return value;
  }
};

type PropertyBag = Record<string, unknown> | undefined;
type CaptureLike = { properties?: PropertyBag; $set?: PropertyBag; $set_once?: PropertyBag };

/** PostHog `before_send` hook: redacts capability URLs in every captured property. */
export const redactCaptureUrls = <T extends CaptureLike | null>(event: T): T => {
  if (!event) return event;
  for (const bag of [event.properties, event.$set, event.$set_once]) {
    if (!bag) continue;
    for (const [key, value] of Object.entries(bag)) {
      if (typeof value === 'string') bag[key] = redactUrl(value);
    }
  }
  return event;
};
